import { useEffect, useState } from "react";
import { PrismButton, PrismDialog, PrismSelect } from "@prism-ui/react";
import type { Project as EditableProjectRecord } from "../../../Projects/projects.utils";
import type { FairEvent, FairProject } from "./ProjectFairs";

interface ApprovedProjectsModalProps {
  show: boolean;
  activeEvent: FairEvent | null;
  candidateProjects: FairProject[];
  selectedProjectToAdd: string;
  onSelectProjectToAdd: (value: string) => void;
  onAddProject: () => void;
  onCreateProject: () => void;
  onEditProject: (project: EditableProjectRecord) => void;
  onRemoveProject: (project: EditableProjectRecord) => void;
  onClose: () => void;
}

// Matches the roster shown on the public fair page and in the printed program, both of
// which list mentees only (mentors are credited separately).
const menteeNames = (project: EditableProjectRecord): string =>
  (project.team?.team ?? [])
    .filter((member) => (member.role ?? "").toLowerCase() === "mentee")
    .map((member) => member.name)
    .join(", ");

// Approved Projects roster: the list of projects assigned to this chapter/year's fair,
// plus controls to add an existing project, create a brand-new one, or edit one in place.
// Sorted alphabetically (case-insensitive) with alternating row shading to make a long
// roster easier to scan.
const ApprovedProjectsModal = ({
  show,
  activeEvent,
  candidateProjects,
  selectedProjectToAdd,
  onSelectProjectToAdd,
  onAddProject,
  onCreateProject,
  onEditProject,
  onRemoveProject,
  onClose,
}: ApprovedProjectsModalProps) => {
  // Id of the row currently showing its "are you sure" prompt. Inline rather than a nested
  // dialog so the confirmation text sits right next to the project it applies to.
  const [confirmingRemoveId, setConfirmingRemoveId] = useState<string | number | null>(null);

  useEffect(() => {
    if (!show) setConfirmingRemoveId(null);
  }, [show]);

  const sortedProjects = [...(activeEvent?.ApprovedProjects || [])].sort((a, b) =>
    (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base" })
  );

  return (
    <PrismDialog open={show} close-method="user" onPrismClose={onClose} setAriaLabel="Manage Projects">
      <span slot="header">Manage Projects</span>
      <div className="project-fairs-modal-body">
      <section className="project-fairs-projects">
        <h3>Selected Projects ({sortedProjects.length})</h3>
        <div className="project-fairs-project-list">
          {sortedProjects.map((project, index) => {
            const mentees = menteeNames(project);
            return (
              <div
                key={project.id}
                className={`project-fairs-project-item ${index % 2 === 1 ? "project-fairs-project-item--alt" : ""}`}
              >
                <div className="project-fairs-project-meta">
                  <div>
                    <strong>{project.title}</strong>{" "}
                    {project.active === false ? <span className="project-fairs-hidden">Hidden</span> : null}
                  </div>
                  <small>Tags: {(project.tags || []).map((entry) => entry.tag.name).join(", ") || "None"}</small>
                  <small>Mentees: {mentees || "None"}</small>
                  {confirmingRemoveId === project.id ? (
                    <p className="project-fairs-remove-confirm">
                      Remove <strong>{project.title}</strong> from this fair&apos;s roster? The project itself is
                      kept — it stays in the project library with its team, tags and logo, and you can add it
                      back to this or any other fair later.
                    </p>
                  ) : null}
                </div>
                <div className="project-fairs-project-actions">
                  {confirmingRemoveId === project.id ? (
                    <>
                      {/* Keys matter here. Without them React reconciles these against the
                          Edit/Remove pair by position and swaps the text of the existing
                          hosts, which wipes the content prism-button relocated into its
                          scoped slot and leaves zero-width, label-less buttons that never
                          recover. Distinct keys force fresh hosts instead. */}
                      <PrismButton
                        key="confirm-remove"
                        tone="danger"
                        display="fill"
                        size="sm"
                        onPrismClick={() => {
                          setConfirmingRemoveId(null);
                          onRemoveProject(project);
                        }}
                      >
                        Remove from fair
                      </PrismButton>
                      <PrismButton
                        key="confirm-keep"
                        tone="secondary"
                        display="outline"
                        size="sm"
                        onPrismClick={() => setConfirmingRemoveId(null)}
                      >
                        Keep
                      </PrismButton>
                    </>
                  ) : (
                    <>
                      <PrismButton key="edit" tone="secondary" display="ghost" size="sm" onPrismClick={() => onEditProject(project)}>
                        Edit
                      </PrismButton>
                      <PrismButton
                        key="remove"
                        tone="danger"
                        display="ghost"
                        size="sm"
                        onPrismClick={() => setConfirmingRemoveId(project.id)}
                      >
                        Remove
                      </PrismButton>
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {!sortedProjects.length ? <p>No projects assigned yet.</p> : null}
        </div>
        <div className="project-fairs-add-row-card">
          <div className="project-fairs-add-row">
            <PrismSelect
              label="Add Existing Project"
              value={selectedProjectToAdd}
              options={[
                { value: "", display: "Select a project" },
                ...candidateProjects.map((project) => ({ value: String(project.id), display: `${project.title} (${project.year})` })),
              ]}
              onPrismChange={(e: CustomEvent<{ value?: string }>) => onSelectProjectToAdd(e.detail?.value || "")}
            />
            <PrismButton tone="secondary" display="fill" onPrismClick={onAddProject}>
              Add Project
            </PrismButton>
          </div>
        </div>
        <PrismButton
          className="project-fairs-add-new-btn"
          tone="primary-brand"
          display="fill"
          onPrismClick={onCreateProject}
        >
          + Add new project
        </PrismButton>
      </section>
      </div>

      <PrismButton slot="footer" tone="secondary" display="outline" onPrismClick={onClose}>
        Close
      </PrismButton>
    </PrismDialog>
  );
};

export default ApprovedProjectsModal;
